import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  acknowledgeIpArrangement, declineIpArrangement, listAggregatorGrants, listMyIpArrangements,
} from '../../services/api'
import { liveByProject } from '../sharing/ipModel'
import IpArrangementCard from './IpArrangementCard'
import { personLabelFor, type Viewer } from './personLabel'
import type { Permission, Project, ProjectOwner } from '../../types'
import type { IpArrangement } from '../../types/ipArrangements'

/**
 * The invention page's right-hand rail (figure S3): the facts about this
 * project, who can see it, and what has happened to it lately.
 *
 * "Who can see this" is the honest, per-project answer to the question the
 * whole sharing model turns on. It names the owner, every collaborator the
 * project has, and every portfolio it has been shared into — with the expiry
 * and the count of folders hidden from that portfolio, because a grant with
 * folders held back is a different fact from a grant without. Nothing here is
 * a summary of a summary: each row is a record the API returned.
 *
 * The IP arrangement sits below it and answers a different question: what was
 * agreed off the platform. It is a record, not a dial — acknowledging one moves
 * nothing in "Who can see this" — which is exactly why it is its own card.
 *
 * Every date, name and count comes from data the page already holds or from
 * this rail's two extra reads (the project's grants, and the caller's own IP
 * arrangements). Where a record is missing a field, the row says so rather
 * than guessing.
 */
export interface ProjectContextRailProps {
  project: Project
  owners?: ProjectOwner[]
  collaborators?: Permission[]
  /** Documents and folders this reader can see, for the "Documents" row. */
  documentCount?: number
  folderCount?: number
  /** The pipeline stage the strip is showing, so the two can never disagree. */
  stageLabel: string
  /** The signed-in person, so they are named here exactly as the workspace
   * switcher names them. Passed in rather than read from context: this card is
   * rendered on its own in tests, and a component that reaches for a provider
   * cannot be. */
  viewer?: Viewer
  /** Real timestamps only — the caller builds them from records that exist. */
  activity?: {
    key: string
    label: string
    detail?: string
    when: string
    /** What KIND of thing happened — the figure gives each row its own glyph. */
    kind?: 'assessment' | 'analysis' | 'document' | 'other'
  }[]
}

function formatDate(iso?: string | number): string | null {
  if (iso == null) return null
  const d = typeof iso === 'number' ? new Date(iso * 1000) : new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' })
}

/** One glyph per kind of thing that happens to an invention (figure S3). */
const ACTIVITY_GLYPH: Record<string, { glyph: string; cls: string }> = {
  assessment: { glyph: '✓', cls: 'bg-emerald-100 text-emerald-700' },
  analysis: { glyph: '🧠', cls: 'bg-sky-100 text-iflow-700' },
  document: { glyph: '📄', cls: 'bg-sky-100 text-iflow-700' },
  other: { glyph: '•', cls: 'bg-slate-100 text-gray-500' },
}

/**
 * The one arrangement this invention should show. A live record (proposed or
 * acknowledged) always wins; when there is none, the newest record still shows,
 * so a decline reads as a decline rather than disappearing into "nothing on
 * record" — history is a fact about the invention too.
 */
function pickArrangement(all: IpArrangement[], projectId: string): IpArrangement | null {
  const live = liveByProject(all)[projectId]
  if (live) return live
  const mine = all
    .filter((a) => a.project_id === projectId)
    .sort((x, y) => (y.proposed_at ?? '').localeCompare(x.proposed_at ?? ''))
  return mine[0] ?? null
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3 text-[12.5px] last:mb-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-right font-semibold text-gray-700">{children}</span>
    </div>
  )
}

export default function ProjectContextRail({
  project,
  owners,
  collaborators,
  documentCount,
  folderCount,
  stageLabel,
  viewer,
  activity,
}: ProjectContextRailProps) {
  const { data: grantsData } = useQuery({
    queryKey: ['aggregator-grants', project.project_id],
    queryFn: () => listAggregatorGrants(project.project_id),
    enabled: !!project.project_id,
    retry: false,
  })
  const grants = grantsData?.grants ?? []

  // The IP arrangements on the caller's own inventions. One read for the whole
  // account (the route takes no argument — the caller is the token), shared by
  // key with any other screen that asks, then narrowed to THIS invention.
  const queryClient = useQueryClient()
  const { data: arrangements } = useQuery({
    queryKey: ['ip-arrangements', 'mine'],
    // Called through a closure, like the grants read above it: a bare function
    // reference is dereferenced while the component renders, which turns a
    // missing export in an older test's module stand-in into a render crash
    // instead of the empty state the card is built to show.
    queryFn: () => listMyIpArrangements(),
    retry: false,
  })
  const arrangement = pickArrangement(arrangements ?? [], project.project_id)
  const refreshArrangements = () =>
    queryClient.invalidateQueries({ queryKey: ['ip-arrangements', 'mine'] })
  const acknowledge = useMutation({
    mutationFn: (id: string) => acknowledgeIpArrangement(id),
    onSuccess: refreshArrangements,
  })
  const decline = useMutation({
    mutationFn: (id: string) => declineIpArrangement(id),
    onSuccess: refreshArrangements,
  })

  const ownerRows = owners ?? []
  // Who a grantee IS, from every record on this page: the grant's own fields
  // first, then the owners route (which now carries the account's display
  // name). "Someone you shared with" is the last honest resort, not the first
  // answer — the card whose job is to say who can see this invention has to
  // name them if anything on the page knows.
  const nameFor = (id?: string | null, email?: string | null, display?: string | null) => {
    const owner = ownerRows.find((o) => o.user_id === id)
    return personLabelFor(
      {
        name: display ?? owner?.name ?? null,
        email: email || owner?.email || null,
        id,
      },
      // The viewer names themselves the way the workspace switcher does. One
      // person cannot read two different names on one screen.
      viewer,
    )
  }
  // A portfolio grant is returned BOTH by the aggregator-grants route and as a
  // permission row with grantee_type 'aggregator'. Rendering both put the same
  // grant on the card twice — once named ("Acme Aggregator") and once not
  // ("Someone you shared with"). The grants route names it, so the permission
  // copy goes.
  const grantIds = new Set(grants.map((g) => g.aggregator_id))
  const people = (collaborators ?? []).filter(
    (c) => c.grantee_type !== 'aggregator' && !grantIds.has(c.grantee_id),
  )
  const primary = ownerRows[0]
  const others = ownerRows.slice(1)
  const created = formatDate(project.created_at)
  const withExclusions = grants.filter((g) => (g.exclusions?.length ?? 0) > 0)
  const hiddenTotal = grants.reduce((n, g) => n + (g.exclusions?.length ?? 0), 0)

  return (
    <div className="space-y-4" data-testid="project-context-rail">
      <section className="light-card p-4">
        <h3 className="mb-2.5 text-[13px] font-bold text-iflow-900">📇 Project details</h3>
        <Row label="Owner">{primary ? nameFor(primary.user_id, primary.email, primary.name) : 'Not recorded'}</Row>
        {others.length > 0 && (
          <Row label={others.length === 1 ? 'Co-inventor' : 'Co-inventors'}>
            {others.map((o) => nameFor(o.user_id, o.email, o.name)).join(', ')}
          </Row>
        )}
        {created && <Row label="Created">{created}</Row>}
        <Row label="Documents">
          {documentCount ?? 0}
          {folderCount ? ` · ${folderCount} folder${folderCount === 1 ? '' : 's'}` : ''}
        </Row>
        <Row label="Stage">
          <span className="rounded-full bg-iflow-100 px-2 py-0.5 text-[11px] font-semibold text-iflow-700">{stageLabel}</span>
        </Row>
        <Row label="Discoverable">{project.discoverable ? 'On' : 'Off'}</Row>
      </section>

      <section className="light-card p-4">
        <h3 className="mb-2 text-[13px] font-bold text-iflow-900">👁 Who can see this</h3>
        <div className="border-b border-sky-100 pb-2">
          <p className="flex items-center gap-2 text-[12.5px] font-semibold text-iflow-900">
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Owner</span>
            You
          </p>
          <p className="mt-0.5 pl-0.5 text-[11.5px] text-gray-500">Everything, always.</p>
        </div>
        {grants.map((grant) => {
          const expires = formatDate(grant.expires_at)
          const hidden = grant.exclusions?.length ?? 0
          return (
            <div key={grant.aggregator_id} className="border-b border-sky-100 py-2">
              <p className="flex items-center gap-2 text-[12.5px] font-semibold text-iflow-900">
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Granted</span>
                {grant.aggregator_name || 'A portfolio you shared with'}
              </p>
              <p className="mt-0.5 pl-0.5 text-[11.5px] text-gray-500">
                View only{expires ? ` · expires ${expires}` : ' · no expiry set'}
                {hidden > 0 ? ` · ${hidden} folder${hidden === 1 ? '' : 's'} hidden` : ''}
              </p>
            </div>
          )
        })}
        {people.map((c) => (
          <div key={`${c.grantee_id}-${c.resource_id}`} className="border-b border-sky-100 py-2">
            <p className="flex items-center gap-2 text-[12.5px] font-semibold text-iflow-900">
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-iflow-700">Shared</span>
              {nameFor(c.grantee_id, c.grantee_email, c.grantee_display_name)}
            </p>
            <p className="mt-0.5 pl-0.5 text-[11.5px] text-gray-500">{c.permission_level} access</p>
          </div>
        ))}
        {grants.length === 0 && people.length === 0 && (
          <p className="border-b border-sky-100 py-2 text-[11.5px] text-gray-500">
            Nobody else. This invention has not been shared with anyone.
          </p>
        )}
        <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500">
          {hiddenTotal > 0
            // Name the portfolio when only one is holding folders back — "hidden
            // from Meridian Ventures" is a fact the reader can act on; "from a
            // portfolio" makes them go looking.
            ? `${hiddenTotal} folder${hiddenTotal === 1 ? '' : 's'} hidden from `
              + `${withExclusions.length === 1 ? (withExclusions[0].aggregator_name || 'a portfolio you shared with') : 'the portfolios you shared with'}`
              + '. Change it under '
            : 'Change any of this under '}
          <Link to="/account/sharing" className="font-bold text-iflow-600 hover:text-iflow-700">
            Who can see what of mine →
          </Link>
        </p>
      </section>

      <IpArrangementCard
        arrangement={arrangement}
        onAcknowledge={(id) => acknowledge.mutateAsync(id)}
        onDecline={(id) => decline.mutateAsync(id)}
      />

      <section className="light-card p-4">
        <h3 className="mb-1 text-[13px] font-bold text-iflow-900">⏱ Recent activity</h3>
        {!activity || activity.length === 0 ? (
          <p className="text-[11.5px] text-gray-500">Nothing has happened to this invention yet.</p>
        ) : (
          <ul>
            {activity.map((item) => (
              <li key={item.key} className="flex items-start gap-2 border-t border-sky-100 py-2 first:border-t-0">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] ${
                    ACTIVITY_GLYPH[item.kind ?? 'other'].cls
                  }`}
                  aria-hidden
                >
                  {ACTIVITY_GLYPH[item.kind ?? 'other'].glyph}
                </span>
                <span className="min-w-0">
                  <span className="block text-[12px] text-iflow-900">
                    <b>{item.label}</b>
                    {item.detail ? ` · ${item.detail}` : ''}
                  </span>
                  <span className="block text-[11.5px] text-gray-500">{item.when}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
